/* eslint-disable unicorn/filename-case */
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Bell01, Building06, LogOut01, User01 } from '@untitled-ui/icons-react';
import { useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppSidebar, SidebarNavSection } from '../../../../components/app-sidebar';
import { Avatar } from '../../../../components/avatar';
import { Badge } from '../../../../components/badge';
import { IconButton } from '../../../../components/buttons/icon-button';
import {
  AppDropdownBody,
  DropdownMenu,
  DropdownSeparator,
} from '../../../../components/dropdown-menu';
import { AnimatedSettingsIcon } from '../../../../components/icons/animated-settings-icon';
import { DropdownMenuOption, MenuOption, MenuOptionIcon } from '../../../../components/menu-option';
import { ModalSignOut } from '../../../../components/modal-sign-out';
import { NavItem } from '../../../../components/sidebar-nav-item';
import { Skeleton } from '../../../../components/skeleton';
import { ToastVariant } from '../../../../components/toast';
import { type Organization } from '../../../../entity/api/organization';
import { type User } from '../../../../entity/api/user';
import { trackError } from '../../../../error/tracking';
import { captureException } from '../../../../logger/capture-exception';
import { getStores, useAuthUser, useOrganizationBilling } from '../../../../services/storage';
import { tGlobal, tNamespace } from '../../../../services/translation';
import { useToast } from '../../../../shared/contexts/toast-context';
import { switchToOrganization } from '../../../../shared/organization-switcher';
import { returnFirstLetter } from '../../../../shared/string';
import { getUserDefaults, layoutUserName } from '../../../../shared/user';

const t = tNamespace('app.settings.sidebar');

function handleLogout() {
  getStores()
    .users.getState()
    .signOut()
    .catch(function (err) {
      captureException(
        err,
        {
          file: 'app/-sidebar.tsx',
          method: 'handleLogout',
          infoMessage: 'unable to logout',
        },
        trackError
      );
    });
}

type OrganizationItemProps = {
  id: string;
  name: string;
  isOwned: boolean;
  isActive: boolean;
};

function OrganizationsItems(props: OrganizationItemProps) {
  const { id, name, isOwned, isActive } = props;
  const navigate = useNavigate();
  const addToast = useToast();
  return (
    <NavItem
      className={twMerge('mb-1 px-3', isActive ? 'pointer-events-none cursor-default' : '')}
      label={name}
      icon={<Building06 width={16} height={16} />}
      secondaryIcon={
        isOwned ? (
          <Badge color="gray" size="sm">
            {t('owned')}
          </Badge>
        ) : null
      }
      onClick={async function () {
        if (isActive) {
          return;
        }
        try {
          const result = await switchToOrganization(id);

          if (result.success) {
            return navigate({
              to: `/app/settings/organization/$id/plan`,
              params: { id },
              replace: true,
              resetScroll: true,
            }).catch(function (err) {
              captureException(
                err,
                {
                  file: 'routes/app/settings/-settings-components/-sidebar.tsx',
                  method: 'OrganizationsItems',
                  infoMessage: 'unable to navigate to organization plan',
                  organizationId: id,
                  organizationName: name,
                },
                trackError
              );
            });
          }

          addToast({
            title: tGlobal('somethingWentWrong'),
            variant: ToastVariant.Error,
            id: Date.now(),
          });
          return captureException(
            new Error(result.error ?? 'Failed to switch organization'),
            {
              file: 'routes/app/settings/-settings-components/-sidebar.tsx',
              method: 'OrganizationsItems',
              infoMessage: 'unable to switch to selected organization',
              organizationId: id,
              organizationName: name,
            },
            trackError
          );
        } catch (err) {
          captureException(
            err,
            {
              file: 'routes/app/settings/-settings-components/-sidebar.tsx',
              method: 'OrganizationsItems',
              infoMessage: 'unable to switch to selected organization',
              organizationId: id,
              organizationName: name,
            },
            trackError
          );
        }
      }}
      isActive={isActive}
    />
  );
}

type SidebarProps = {
  organizationData: {
    organization: Organization;
    isOwner: boolean;
    isActive: boolean;
  }[];
  variant?: 'desktop' | 'mobile';
};

type UserSettingsDropdownProps = {
  user?: {
    firstName: User['firstName'];
    lastName: User['lastName'];
    email: User['email'];
  };
};

function UserSettingsDropdown(props: UserSettingsDropdownProps) {
  const { user } = props;
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openModalSignOut, setOpenModalSignOut] = useState(false);

  // Handle modal close and also close dropdown
  const handleModalSignOutChange = useCallback(function (open: boolean) {
    setOpenModalSignOut(open);
  }, []);

  if (user == null) {
    return <NavItem isLoading={true} label={''} />;
  }

  return (
    <>
      <ModalSignOut
        open={openModalSignOut}
        onOpenChange={handleModalSignOutChange}
        title={tGlobal('confirmSignOut')}
        onSignOut={handleLogout}
      />
      <DropdownMenu
        position="start"
        side="top"
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        trigger={
          <MenuOption
            mainText={layoutUserName(user, true)}
            className="group/settings-trigger max-h-[36px] w-full"
            leftElement={
              <Avatar
                size="sm"
                color="accent-contrast"
                initials={`${returnFirstLetter(getUserDefaults(user).firstName)}`}
              />
            }
            rightElement={
              <MenuOptionIcon variant="gray">
                <AnimatedSettingsIcon
                  width={16}
                  height={16}
                  groupName="group/settings-trigger"
                  bgColor="#F3F5FB"
                />
              </MenuOptionIcon>
            }
          />
        }
      >
        <AppDropdownBody>
          <DropdownMenuOption
            mainText={layoutUserName(user, true)}
            secondaryText={`${user.email}`}
            className="hover:bg-branding-gray-100"
            onClick={function () {
              navigate({ to: '/app/settings/user' }).catch(function (err) {
                captureException(
                  err,
                  {
                    file: 'app/-sidebar.tsx',
                    method: 'navigate',
                    infoMessage: 'unable to navigate to /app/settings/user',
                  },
                  trackError
                );
              });
            }}
            leftElement={
              <Avatar
                size="md"
                color="accent"
                initials={`${returnFirstLetter(getUserDefaults(user).firstName)}`}
              />
            }
          />
          <DropdownSeparator />
          <NavItem
            label={tGlobal('logout')}
            icon={<LogOut01 width={16} height={16} className="text-red-700" />}
            onClick={function () {
              setOpenModalSignOut(true);
              setDropdownOpen(false);
            }}
            variant="red"
          />
        </AppDropdownBody>
      </DropdownMenu>
    </>
  );
}

export function SidebarSettings(props: SidebarProps) {
  const { organizationData, variant } = props;
  const { pathname } = useLocation();
  const currOrg = organizationData.find((o) => o.isActive)?.organization;
  const { data: currentOrgBilling } = useOrganizationBilling(currOrg?._id ?? '');
  const { data: authUser } = useAuthUser();
  return (
    <AppSidebar
      currentPath={pathname}
      currentOrganization={currOrg}
      currentOrgBilling={currentOrgBilling}
      variant={variant ?? 'desktop'}
      navigator={
        <Link to="/app" className="mb-6 flex items-center gap-2">
          <IconButton isDiv icon={ArrowLeft} variant="outline" />
          <p className="text-xl font-bold">{tGlobal('settings')}</p>
        </Link>
      }
      navSections={[
        <SidebarNavSection title={t('organizations')} key="organization">
          {organizationData?.length != null ? (
            <div className="w-full">
              {organizationData.map(function (org) {
                return (
                  <OrganizationsItems
                    key={org.organization._id}
                    id={org.organization._id}
                    name={org.organization.name}
                    isOwned={org.isOwner}
                    isActive={org.isActive}
                  />
                );
              })}
            </div>
          ) : (
            <Skeleton className="flex h-8 w-full rounded-full" />
          )}
        </SidebarNavSection>,
        <SidebarNavSection title={t('you')} key="you">
          <NavItem
            className="px-3"
            label={t('profileSettings')}
            isActive={pathname.includes('/app/settings/user')}
            icon={<User01 width={16} height={16} />}
            href="/app/settings/user"
          />
          <NavItem
            className="px-3"
            label={t('notifications')}
            isActive={pathname.includes('/app/settings/notifications')}
            icon={<Bell01 width={16} height={16} />}
            href="/app/settings/notifications"
          />
        </SidebarNavSection>,
      ]}
      footer={<UserSettingsDropdown user={authUser} />}
    />
  );
}
